import { buildLocalStorageKey } from '@risksmart-app/components/src/hooks/useLocalStorage';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { useEffect } from 'react';
import { defaultDashboardFilter } from 'src/context/defaultDashboardFilter';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useOrgScopedLocalStorage } from '@/hooks/useOrgScopedLocalStorage';
import { handleError } from '@/utils/errorUtils';

import {
  defaultMyItemsDashboardLayout,
  defaultOverallDashboardLayout,
} from './defaultLayout';
import type { State } from './useDashboardStore';
import { dashboardStore, defaultMyItemsFilter } from './useDashboardStore';

const STORAGE_KEY = 'Dashboard-PreferencesV3';

export type PersistedDashboardPreferences = Omit<State, 'selectedDashboard'>;

/**
 * Writes straight to localStorage rather than through
 * `useOrgScopedLocalStorage`'s setter. That setter also sets React state in
 * whichever component owns the hook — here `Page` — so using it would re-render
 * `Page`, and therefore the whole board, on every filter change. The read side
 * still goes through the hook because it owns the parse and error handling.
 */
const writePreferences = (
  storageKey: string,
  preferences: PersistedDashboardPreferences
) => {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(preferences));
  } catch (error) {
    handleError(error);
  }
};

/**
 * The dashboard's ONE localStorage seam. Mounted once, in
 * `pages/dashboards/Page.tsx`. It reads the persisted blob on mount, hydrates
 * the store from it, and then writes back through `dashboardStore.subscribe`.
 *
 * Do not mount it anywhere else and do not reintroduce a per-consumer
 * hydration effect: before ENG-559 every consumer of `useDashboardStore` owned
 * one, so a 15-widget board did ~50 localStorage reads and ~50 whole-store
 * hydrations during mount, each one broadcasting to every subscriber.
 *
 * Subscribing AFTER hydration is deliberate — it means the hydrating write does
 * not echo straight back out to localStorage.
 */
export const useDashboardPreferencesSync = () => {
  const emptyDefaultDashboard = useIsFeatureFlagEnabled(
    'empty_default_dashboard'
  );
  const { user } = useRisksmartUser();
  const [storedValue] = useOrgScopedLocalStorage<PersistedDashboardPreferences>(
    {
      filters: defaultDashboardFilter,
      widgets: emptyDefaultDashboard ? [] : defaultOverallDashboardLayout,
      myItemsWidgets: defaultMyItemsDashboardLayout,
      myItemsFilters: defaultMyItemsFilter,
    },
    {
      localStorageKey: STORAGE_KEY,
    }
  );

  useEffect(() => {
    // Derived with the same builder `useLocalStorage` reads through, so the
    // direct write below cannot drift from the hook's read above. The dashboard
    // only renders under `ProtectedLayout`, which redirects a user without an
    // organisation, so `orgKey` is resolved by the time this mounts.
    const storageKey = buildLocalStorageKey(STORAGE_KEY, user?.orgKey);

    // `id` is deliberately absent from the hydration payload: the store action
    // leaves `id` untouched when the payload has no `id` key, which is exactly
    // what the pre-ENG-559 hydration did. Hydrating it would silently reload the
    // last-selected saved dashboard on every page load.
    dashboardStore.getState().actions.setDashboardPreferences(
      {
        filters: storedValue.filters,
        widgets: storedValue.widgets,
      },
      storedValue.myItemsWidgets ?? defaultMyItemsDashboardLayout,
      // Per-key backfill: a persisted blob saved before the 8 ownership flags
      // existed lacks some keys. Spread defaults first so only ABSENT keys fall
      // back — any flag actually stored (even `false`) is preserved. Guards the
      // tRPC ribbon input schema against `undefined` flags (RISKSMART-APP-A08).
      { ...defaultMyItemsFilter, ...storedValue.myItemsFilters }
    );

    return dashboardStore.subscribe((state, previousState) => {
      // `selectedDashboard` is not persisted, so a view toggle must not trigger
      // a `JSON.stringify` of the whole blob.
      if (
        state.filters === previousState.filters &&
        state.widgets === previousState.widgets &&
        state.myItemsFilters === previousState.myItemsFilters &&
        state.myItemsWidgets === previousState.myItemsWidgets &&
        state.id === previousState.id
      ) {
        return;
      }

      writePreferences(storageKey, {
        id: state.id,
        filters: state.filters,
        widgets: state.widgets,
        myItemsWidgets: state.myItemsWidgets,
        myItemsFilters: state.myItemsFilters,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
